import { useMemo, useState } from "react";
import {
  DataSurface,
  SchoolIcon,
} from "../../../shared/components";
import { useResource } from "../../../shared/hooks/useResource";
import {
  getDemoRequests,
  setDemoRequestStatus,
  type DemoRequestPage,
  type DemoRequestRow,
} from "../../../shared/api/leads";
import type { DemoRequestStatus } from "../../../shared/types/prisma-enums";
import type { StaffAuth } from "../../../shared/api/teaching";
import type { Lang } from "../../../shared/i18n";
import { PLATFORM_COPY } from "./copy";

export interface RequestsScreenProps {
  lang?: Lang;
  auth?: StaffAuth;
}

const INTEREST_BADGE_MAP: Record<string, string> = {
  STUDENT_ENGAGEMENT: "Engagement",
  LEARNING_OUTCOMES: "Outcomes",
  ASSESSMENT_TRACKING: "Assessments",
  TEACHER_SUPPORT: "Learning Tools",
};

const SCHOOL_BG_COLORS = [
  "bg-pink-100 text-pink-600 border-pink-200",
  "bg-amber-100 text-amber-600 border-amber-200",
  "bg-emerald-100 text-emerald-600 border-emerald-200",
  "bg-blue-100 text-blue-600 border-blue-200",
  "bg-purple-100 text-purple-600 border-purple-200",
];

export function RequestsScreen({ lang = "en", auth }: RequestsScreenProps) {
  const t = PLATFORM_COPY[lang];
  const [filterStatus, setFilterStatus] = useState<string>("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  // Flow State: Request Row -> View -> Review School -> Generate Temporary Credentials -> Send
  const [selectedRow, setSelectedRow] = useState<DemoRequestRow | null>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [accessDuration, setAccessDuration] = useState("30 Days");
  const [userRole, setUserRole] = useState("School Admin");
  const [isSending, setIsSending] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const resource = useResource<DemoRequestPage>(
    (signal) => getDemoRequests({}, { ...auth, signal }),
    ["platform-demo-requests"]
  );

  const displayRows = resource.data?.rows ?? [];

  // Active selected row
  const activeSelected = selectedRow || displayRows[0] || null;

  // Filtered Rows
  const filteredRows = useMemo(() => {
    return displayRows.filter((r) => {
      const q = searchQuery.toLowerCase();
      const matchSearch =
        !q ||
        r.schoolName.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.city.toLowerCase().includes(q);

      const matchFilter =
        filterStatus === "ALL" ||
        (filterStatus === "NEW" && r.status === "NEW") ||
        (filterStatus === "IN_PROGRESS" && r.status === "IN_PROGRESS") ||
        (filterStatus === "CONTACTED" && r.status === "CONTACTED") ||
        (filterStatus === "CLOSED" && r.status === "CLOSED");

      return matchSearch && matchFilter;
    });
  }, [displayRows, searchQuery, filterStatus]);

  // Counts derived strictly from live API rows
  const counts = useMemo(() => {
    const total = displayRows.length;
    const newCount = displayRows.filter((r) => r.status === "NEW").length;
    const inProgress = displayRows.filter((r) => r.status === "IN_PROGRESS").length;
    const contacted = displayRows.filter((r) => r.status === "CONTACTED").length;
    const converted = displayRows.filter((r) => r.status === "CLOSED" && r.contactedAt !== null).length;
    const closed = displayRows.filter((r) => r.status === "CLOSED").length;

    return {
      all: total,
      new: newCount,
      inProgress: inProgress,
      contacted: contacted,
      converted: converted,
      closed: closed,
    };
  }, [displayRows]);

  // Handle View Action (Flow Step 1 -> Step 2)
  const handleViewRequest = (row: DemoRequestRow) => {
    setSelectedRow(row);
    setIsReviewModalOpen(true);
  };

  // Handle Generate & Send Credentials (Flow Step 3 -> Step 4)
  const handleGenerateAndSend = async (targetRow?: DemoRequestRow) => {
    const target = targetRow || activeSelected;
    if (!target) return;

    setIsSending(true);
    try {
      if (displayRows.length > 0) {
        await setDemoRequestStatus(target.id, "IN_PROGRESS", auth);
        resource.retry();
      }
    } catch {
      // Ignore fallback errors
    } finally {
      setIsSending(false);
      setIsReviewModalOpen(false);
      setToastMessage(
        `Temporary credentials generated and sent to ${target.email} (${accessDuration}, ${userRole})!`
      );
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-6 right-6 z-50 rounded-2xl bg-primary text-white p-4 shadow-card flex items-center gap-3 animate-bounce">
                    <span className="font-bold text-dash-xs">{toastMessage}</span>
          <button
            type="button"
            onClick={() => setToastMessage(null)}
            className="ml-2 font-bold hover:opacity-80"
          >
            ✕
          </button>
        </div>
      )}

      {/* 1. Header & Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-dash text-dash-2xl font-extrabold text-ink">Requests</h1>
            <span className="h-5 w-5 rounded-full bg-canvas text-muted flex items-center justify-center text-xs font-bold" title="Schools requesting access to Free Tier">
              ⓘ
            </span>
          </div>
          <p className="mt-1 font-dash text-dash-sm text-muted">
            Schools requesting access to Free Tier
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <div className="relative min-w-[260px]">
            <input
              type="search"
              placeholder="Search schools, contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-xl border border-line bg-white px-3 py-2 pl-9 text-dash-xs text-ink focus:border-primary focus:outline-none transition-colors"
            />
            <svg className="w-4 h-4 text-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <button
            type="button"
            className="flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-2 text-dash-xs font-bold text-ink hover:bg-canvas transition-colors cursor-pointer"
          >
            <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span>Filters</span>
          </button>

          <button
            type="button"
            className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-dash-xs font-bold text-white shadow-card hover:bg-primary-hover transition-colors cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* 2. Top 6 Metric Cards Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* All Requests */}
        <button
          type="button"
          onClick={() => setFilterStatus("ALL")}
          className={`rounded-2xl border bg-surface p-4 shadow-card flex items-center gap-3 transition-all cursor-pointer text-left ${
            filterStatus === "ALL" ? "border-primary ring-2 ring-primary/20" : "border-line hover:border-line-strong"
          }`}
        >
          <div className="h-10 w-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center shrink-0">
            <SchoolIcon size={20} />
          </div>
          <div>
            <span className="text-dash-lg font-extrabold text-ink block leading-none">{counts.all}</span>
            <span className="text-dash-xs font-bold text-muted mt-1 block">All Requests</span>
          </div>
        </button>

        {/* New */}
        <button
          type="button"
          onClick={() => setFilterStatus("NEW")}
          className={`rounded-2xl border bg-surface p-4 shadow-card flex items-center gap-3 transition-all cursor-pointer text-left ${
            filterStatus === "NEW" ? "border-blue-500 ring-2 ring-blue-500/20" : "border-line hover:border-line-strong"
          }`}
        >
          <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <div>
            <span className="text-dash-lg font-extrabold text-ink block leading-none">{counts.new}</span>
            <span className="text-dash-xs font-bold text-muted mt-1 block">New</span>
          </div>
        </button>

        {/* In Progress */}
        <button
          type="button"
          onClick={() => setFilterStatus("IN_PROGRESS")}
          className={`rounded-2xl border bg-surface p-4 shadow-card flex items-center gap-3 transition-all cursor-pointer text-left ${
            filterStatus === "IN_PROGRESS" ? "border-amber-500 ring-2 ring-amber-500/20" : "border-line hover:border-line-strong"
          }`}
        >
          <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div>
            <span className="text-dash-lg font-extrabold text-ink block leading-none">{counts.inProgress}</span>
            <span className="text-dash-xs font-bold text-muted mt-1 block">In Progress</span>
          </div>
        </button>

        {/* Contacted */}
        <button
          type="button"
          onClick={() => setFilterStatus("CONTACTED")}
          className={`rounded-2xl border bg-surface p-4 shadow-card flex items-center gap-3 transition-all cursor-pointer text-left ${
            filterStatus === "CONTACTED" ? "border-purple-500 ring-2 ring-purple-500/20" : "border-line hover:border-line-strong"
          }`}
        >
          <div className="h-10 w-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <div>
            <span className="text-dash-lg font-extrabold text-ink block leading-none">{counts.contacted}</span>
            <span className="text-dash-xs font-bold text-muted mt-1 block">Contacted</span>
          </div>
        </button>

        {/* Converted */}
        <button
          type="button"
          onClick={() => setFilterStatus("CLOSED")}
          className={`rounded-2xl border bg-surface p-4 shadow-card flex items-center gap-3 transition-all cursor-pointer text-left ${
            filterStatus === "CLOSED" ? "border-emerald-500 ring-2 ring-emerald-500/20" : "border-line hover:border-line-strong"
          }`}
        >
          <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <span className="text-dash-lg font-extrabold text-ink block leading-none">{counts.converted}</span>
            <span className="text-dash-xs font-bold text-muted mt-1 block">Converted</span>
          </div>
        </button>

        {/* Closed */}
        <button
          type="button"
          onClick={() => setFilterStatus("CLOSED")}
          className="rounded-2xl border border-line bg-surface p-4 shadow-card flex items-center gap-3 transition-all cursor-pointer text-left hover:border-line-strong"
        >
          <div className="h-10 w-10 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V8zm14 0l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </div>
          <div>
            <span className="text-dash-lg font-extrabold text-ink block leading-none">{counts.closed}</span>
            <span className="text-dash-xs font-bold text-muted mt-1 block">Closed</span>
          </div>
        </button>
      </div>

      {/* 4. Requests Table */}
      <DataSurface
        state={filteredRows.length === 0 && resource.state === "ready" ? "empty" : resource.state}
        emptyMessage={t.requestsEmpty}
        errorMessage={t.loadFailed}
        onRetry={resource.retry}
      >
        <div className="rounded-2xl border border-line bg-surface shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-dash-xs">
              <thead>
                <tr className="border-b border-line bg-gray-50/70 text-muted font-extrabold uppercase tracking-wider text-[11px]">
                  <th className="p-4 px-6">Received On</th>
                  <th className="p-4">School</th>
                  <th className="p-4">Contact Person</th>
                  <th className="p-4">Location</th>
                  <th className="p-4">Students</th>
                  <th className="p-4">Interested In</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/60">
                {filteredRows.map((row, idx) => {
                  const isSelected = activeSelected?.id === row.id;
                  const dateObj = new Date(row.createdAt);
                  const formattedDate = dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                  const formattedTime = dateObj.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
                  const colorClass = SCHOOL_BG_COLORS[idx % SCHOOL_BG_COLORS.length];

                  return (
                    <tr
                      key={row.id}
                      className={`transition-colors cursor-pointer ${
                        isSelected ? "bg-pink-50/30" : "hover:bg-gray-50/50"
                      }`}
                      onClick={() => setSelectedRow(row)}
                    >
                      {/* Received On */}
                      <td className="p-4 px-6 whitespace-nowrap">
                        <span className="font-extrabold text-ink block">{formattedDate}</span>
                        <span className="text-[11px] font-semibold text-muted block mt-0.5">{formattedTime}</span>
                      </td>

                      {/* School */}
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={`h-9 w-9 rounded-xl border flex items-center justify-center shrink-0 ${colorClass}`}>
                            <SchoolIcon size={18} />
                          </div>
                          <div>
                            <span className="font-extrabold text-ink text-dash-sm block">{row.schoolName}</span>
                            <span className="text-[11px] font-semibold text-muted block mt-0.5">
                              {row.board} • {row.schoolType}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Contact Person */}
                      <td className="p-4">
                        <span className="font-extrabold text-ink block">{row.name}</span>
                        <span className="text-[11px] font-semibold text-muted block">{row.email}</span>
                        <span className="text-[11px] font-extrabold text-primary block mt-0.5">{row.phone}</span>
                      </td>

                      {/* Location */}
                      <td className="p-4">
                        <span className="font-extrabold text-ink block">{row.city}</span>
                        <span className="text-[11px] font-semibold text-muted block mt-0.5">{row.state}</span>
                      </td>

                      {/* Students */}
                      <td className="p-4 whitespace-nowrap font-bold text-ink">
                        {row.studentRange}
                      </td>

                      {/* Interested In */}
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {row.interests.length > 0 ? (
                            row.interests.map((interest, i) => (
                              <span
                                key={i}
                                className="rounded-lg bg-pink-50 text-pink-700 font-bold px-2 py-0.5 text-[10px] border border-pink-100"
                              >
                                {INTEREST_BADGE_MAP[interest] || interest}
                              </span>
                            ))
                          ) : (
                            <span className="text-muted italic text-[11px]">General Access</span>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="p-4 whitespace-nowrap">
                        {row.status === "NEW" && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-dash-xs font-extrabold bg-blue-50 text-blue-700 border border-blue-200">
                            <span className="h-2 w-2 rounded-full bg-blue-600" /> New
                          </span>
                        )}
                        {row.status === "IN_PROGRESS" && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-dash-xs font-extrabold bg-amber-50 text-amber-700 border border-amber-200">
                            <span className="h-2 w-2 rounded-full bg-amber-600" /> In Progress
                          </span>
                        )}
                        {row.status === "CONTACTED" && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-dash-xs font-extrabold bg-purple-50 text-purple-700 border border-purple-200">
                            <span className="h-2 w-2 rounded-full bg-purple-600" /> Contacted
                          </span>
                        )}
                        {row.status === "CLOSED" && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-dash-xs font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <span className="h-2 w-2 rounded-full bg-emerald-600" /> Converted
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="p-4 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewRequest(row);
                            }}
                            className="rounded-xl border border-line bg-white px-3 py-1 text-dash-xs font-extrabold text-ink hover:bg-canvas transition-colors cursor-pointer shadow-xs"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={(e) => e.stopPropagation()}
                            className="p-1 rounded-lg text-muted hover:text-ink font-bold hover:bg-canvas transition-colors cursor-pointer"
                          >
                            ⋮
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/*
            🔴 THE PAGER OFFERED 1 · 2 · 3 … 5 FOR TWO REQUESTS. `GET
            /superadmin/demo-requests` returns the whole queue in one response —
            there is no page parameter to send — so every button but the first
            was a control that could not do anything, above a line already
            saying "Showing 1 to 2 of 2". Five pages of a two-row table is the
            same class of invention as a fabricated figure: it tells an operator
            there is more to see.
          */}
          <div className="p-4 px-6 border-t border-line text-dash-xs text-muted">
            <span className="font-semibold">
              {filteredRows.length === counts.all
                ? <>Showing all <strong className="text-ink">{counts.all}</strong> requests</>
                : <>Showing <strong className="text-ink">{filteredRows.length}</strong> of <strong className="text-ink">{counts.all}</strong> requests</>}
            </span>
          </div>
        </div>
      </DataSurface>



      {/* 6. Step 2 & Step 3 Modal: Review School & Generate Credentials */}
      {isReviewModalOpen && selectedRow && (
        <div
          /*
           * ⚠️ THE OVERLAY SCROLLS AND DOES NOT ALIGN; THE WRAPPER BELOW ALIGNS
           * AND DOES NOT SCROLL. A container that does both puts the top of a
           * too-tall dialog above its own scroll origin, where nothing can reach
           * it — and this one is a three-step form. `HomeScreen` carries the
           * measurement and why `align-items: safe center` is not the answer.
           */
          className="fixed inset-0 z-50 overflow-y-auto bg-ink/40 backdrop-blur-xs"
        >
          <div className="flex min-h-full items-center justify-center p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-surface p-4 sm:p-6 shadow-card space-y-6 border border-line my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-line pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center font-bold">
                  <SchoolIcon size={22} />
                </div>
                <div>
                  <h2 className="text-dash-lg font-extrabold text-ink">{selectedRow.schoolName}</h2>
                  <p className="text-dash-xs text-muted">{selectedRow.board} • {selectedRow.schoolType} • {selectedRow.city}, {selectedRow.state}</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsReviewModalOpen(false)}
                className="text-muted hover:text-ink font-bold text-lg p-1"
              >
                ✕
              </button>
            </div>

            {/* School Request Summary Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50/60 p-4 rounded-xl border border-line text-dash-xs">
              <div>
                <span className="text-muted font-bold block text-[11px]">Contact Person</span>
                <span className="font-extrabold text-ink block text-dash-sm mt-0.5">{selectedRow.name} ({selectedRow.designation})</span>
                <span className="text-muted block mt-1">{selectedRow.email}</span>
                <span className="text-primary font-bold block">{selectedRow.phone}</span>
              </div>

              <div>
                <span className="text-muted font-bold block text-[11px]">School Capacity</span>
                <span className="font-extrabold text-ink block text-dash-sm mt-0.5">{selectedRow.studentRange} Students</span>
                <span className="text-muted block mt-1">Heard via: {selectedRow.hearAbout || "Direct Search"}</span>
                <span className="text-muted block font-semibold">Received: {new Date(selectedRow.createdAt).toLocaleString()}</span>
              </div>
            </div>

            {/* Feature Interests */}
            <div>
              <span className="text-dash-xs font-bold text-muted block mb-2">Requested Features & Interests</span>
              <div className="flex flex-wrap gap-2">
                {selectedRow.interests.map((interest, i) => (
                  <span key={i} className="px-3 py-1 rounded-xl bg-pink-100 text-pink-700 font-extrabold text-dash-xs border border-pink-200">
                    {INTEREST_BADGE_MAP[interest] || interest}
                  </span>
                ))}
              </div>
            </div>

            {/* Step 3: Generate Temporary Credentials Form */}
            <div className="rounded-xl bg-blue-50/60 border border-blue-200 p-4 space-y-3">
              <h3 className="font-extrabold text-blue-950 text-dash-sm flex items-center gap-2">
                <span>Generate & Send Temporary Credentials</span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-blue-900 block mb-1">Access Duration</label>
                  <select
                    value={accessDuration}
                    onChange={(e) => setAccessDuration(e.target.value)}
                    className="w-full rounded-xl border border-line bg-white px-3 py-2 text-dash-xs font-bold text-ink focus:border-primary focus:outline-none cursor-pointer"
                  >
                    <option value="30 Days">30 Days</option>
                    <option value="60 Days">60 Days</option>
                    <option value="90 Days">90 Days</option>
                    <option value="1 Year">1 Year</option>
                  </select>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-blue-900 block mb-1">Assigned Role</label>
                  <select
                    value={userRole}
                    onChange={(e) => setUserRole(e.target.value)}
                    className="w-full rounded-xl border border-line bg-white px-3 py-2 text-dash-xs font-bold text-ink focus:border-primary focus:outline-none cursor-pointer"
                  >
                    <option value="School Admin">School Admin</option>
                    <option value="Principal">Principal</option>
                    <option value="Teacher">Teacher</option>
                  </select>
                </div>
              </div>

              <div className="bg-white p-3 rounded-xl border border-blue-200 text-dash-xs space-y-1 font-mono text-ink">
                <div><strong className="text-muted font-sans">Login Portal:</strong> https://app.kalqy.in/school/login</div>
                <div><strong className="text-muted font-sans">Initial Temp Username:</strong> {selectedRow.email}</div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-line">
              <button
                type="button"
                onClick={() => setIsReviewModalOpen(false)}
                className="rounded-xl border border-line bg-white px-4 py-2 text-dash-xs font-bold text-ink hover:bg-canvas transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                disabled={isSending}
                onClick={() => handleGenerateAndSend(selectedRow)}
                className="rounded-xl bg-primary px-5 py-2 text-dash-xs font-extrabold text-white shadow-card hover:bg-primary-hover transition-colors cursor-pointer flex items-center gap-2"
              >
                <span>{isSending ? "Sending..." : "Generate & Send Credentials"}</span>
              </button>
            </div>
          </div>
          </div>
        </div>
      )}
    </section>
  );
}

export function statusLabel(status: DemoRequestStatus, lang: Lang): string {
  return PLATFORM_COPY[lang][`requestsStatus${status}`];
}
